import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Upload, Trash2, GripVertical, Play, Image, X,
  ChevronLeft, ChevronRight, ZoomIn, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';
import { resolveMediaUrl } from '../../../utils/helpers';
import { handleUploadError, uploadErrorMessage } from '../../../services/imageUploadService';

const GallerySettings = ({ academyId }) => {
  const { theme } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!academyId) return;
    academyAPI.gallery.list(academyId)
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load gallery'))
      .finally(() => setLoading(false));
  }, [academyId]);

  // Lightbox keyboard nav
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(() => setLightboxIdx(i => (i - 1 + items.length) % items.length), [items.length]);
  const next = useCallback(() => setLightboxIdx(i => (i + 1) % items.length), [items.length]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, prev, next, closeLightbox]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(file => academyAPI.gallery.upload(academyId, file, null))
      );
      setItems(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? 's' : ''} uploaded`);
    } catch (err) {
      // Same handling posts use: handleUploadError shows the content-policy
      // modal for a Rekognition rejection and returns true, so only anything it
      // doesn't own falls through to a toast.
      console.error('Gallery upload failed:', err);
      if (!handleUploadError(err)) {
        toast.error(uploadErrorMessage(err));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (idx) => {
    const item = items[idx];
    setDeleteConfirm(null);
    if (lightboxIdx !== null) {
      if (idx === lightboxIdx) closeLightbox();
      else if (idx < lightboxIdx) setLightboxIdx(i => i - 1);
    }
    setItems(prev => prev.filter((_, i) => i !== idx));
    try {
      await academyAPI.gallery.remove(academyId, item.id);
    } catch {
      setItems(prev => { const n = [...prev]; n.splice(idx, 0, item); return n; });
      toast.error('Failed to delete item');
    }
  };

  const handleDownload = async (item, idx) => {
    const src = resolveMediaUrl(item.url);
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gallery-${idx + 1}.${item.type === 'video' ? 'mp4' : 'jpg'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(src, '_blank');
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver  = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop      = async (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setItems(reordered);
    setDragIdx(null);
    setDragOverIdx(null);
    try {
      await academyAPI.gallery.reorder(academyId, reordered.map(it => it.id));
    } catch {
      // non-critical — visual order already updated
    }
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const currentItem = lightboxIdx !== null ? items[lightboxIdx] : null;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`text-lg font-semibold ${theme.text}`}>Gallery</h3>
          <p className={`text-sm ${theme.textMuted} mt-0.5`}>
            Upload photos and videos that appear on your academy page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 1 && (
            <button
              onClick={() => setReorderMode(r => !r)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                reorderMode
                  ? 'bg-green-500 hover:bg-green-600 text-white border-transparent'
                  : `${theme.card} ${theme.border} ${theme.text} ${theme.cardHover}`
              }`}
            >
              <GripVertical size={14} />
              {reorderMode ? 'Done Reordering' : 'Reorder'}
            </button>
          )}
          {!reorderMode && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Upload size={14} />
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleUpload}
              />
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div
          className={`border-2 border-dashed ${theme.border} rounded-xl py-16 text-center cursor-pointer hover:border-blue-400 transition-colors`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Image size={40} className={`mx-auto mb-3 ${theme.textMuted} opacity-40`} />
          <p className={`font-medium ${theme.text} mb-1`}>No photos or videos yet</p>
          <p className={`text-sm ${theme.textMuted}`}>Click to upload your first item</p>
        </div>
      ) : reorderMode ? (
        /* ── Reorder grid ── */
        <div>
          <p className={`text-xs ${theme.textMuted} mb-3`}>
            Drag to reorder. Click "Done Reordering" when finished — order saves automatically.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((item, i) => (
              <div
                key={item.id}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={() => handleDrop(i)}
                onDragEnd={handleDragEnd}
                className={`relative rounded-xl overflow-hidden bg-black cursor-grab active:cursor-grabbing transition-all duration-150 ${
                  dragIdx === i ? 'opacity-40 scale-95' : ''
                } ${
                  dragOverIdx === i && dragIdx !== i ? 'ring-2 ring-blue-500 ring-offset-2 scale-105' : ''
                }`}
                style={{ aspectRatio: '1' }}
              >
                {item.type === 'video' ? (
                  <video src={resolveMediaUrl(item.url)} className="w-full h-full object-cover" muted preload="metadata" />
                ) : (
                  <img src={resolveMediaUrl(item.url)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                )}
                <div className="absolute inset-0 bg-black/20 flex flex-col items-center justify-between p-2">
                  <div className="self-start bg-black/50 rounded-full p-1">
                    <GripVertical size={14} className="text-white" />
                  </div>
                  {item.type === 'video' && (
                    <div className="bg-black/50 rounded-full p-1.5">
                      <Play size={14} className="text-white fill-white" />
                    </div>
                  )}
                  <span className="text-white/60 text-xs self-end">{i + 1}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ── Management grid ── */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((item, i) => (
            <div
              key={item.id}
              className="relative group cursor-pointer rounded-xl overflow-hidden bg-black"
              style={{ aspectRatio: '1' }}
              onClick={() => setLightboxIdx(i)}
            >
              {item.type === 'video' ? (
                <video src={resolveMediaUrl(item.url)} className="w-full h-full object-cover" muted preload="metadata" />
              ) : (
                <img src={resolveMediaUrl(item.url)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              )}

              {item.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="p-2.5 bg-black/50 rounded-full">
                    <Play size={16} className="text-white fill-white" />
                  </div>
                </div>
              )}

              {/* Hover controls */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-200 flex items-center justify-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(i); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-white/20 hover:bg-white/40 rounded-full backdrop-blur-sm"
                  title="Preview"
                >
                  {item.type === 'video' ? <Play size={15} className="text-white fill-white" /> : <ZoomIn size={15} className="text-white" />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(item, i); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-white/20 hover:bg-white/40 rounded-full backdrop-blur-sm"
                  title="Download"
                >
                  <Download size={15} className="text-white" />
                </button>
                {deleteConfirm === i ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(i); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 bg-red-500 hover:bg-red-600 rounded-lg text-white text-xs font-semibold"
                  >
                    Confirm?
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(i); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-white/20 hover:bg-red-500/70 rounded-full backdrop-blur-sm"
                    title="Delete"
                  >
                    <Trash2 size={15} className="text-white" />
                  </button>
                )}
              </div>

              {/* Caption */}
              {item.caption && (
                <div className="absolute bottom-0 inset-x-0 bg-black/60 px-2 py-1">
                  <p className="text-white text-xs truncate">{item.caption}</p>
                </div>
              )}
            </div>
          ))}

          {/* Upload tile */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={`rounded-xl border-2 border-dashed ${theme.border} hover:border-blue-400 transition-colors flex flex-col items-center justify-center gap-2 ${theme.textMuted} hover:text-blue-400`}
            style={{ aspectRatio: '1' }}
          >
            <Upload size={20} />
            <span className="text-xs font-medium">{uploading ? 'Uploading…' : 'Add more'}</span>
          </button>
        </div>
      )}

      {/* Item count */}
      {items.length > 0 && !loading && (
        <p className={`text-xs ${theme.textMuted} mt-4`}>
          {items.length} item{items.length !== 1 ? 's' : ''} in gallery
        </p>
      )}

      {/* Lightbox */}
      {currentItem && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
            {lightboxIdx + 1} / {items.length}
          </div>
          <button onClick={closeLightbox} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
            <X size={20} className="text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(currentItem, lightboxIdx); }}
            className="absolute top-4 right-14 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            title="Download"
          >
            <Download size={20} className="text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <ChevronLeft size={28} className="text-white" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-[85vw] max-h-[85vh] flex items-center justify-center">
            {currentItem.type === 'video' ? (
              <video
                key={currentItem.url}
                src={resolveMediaUrl(currentItem.url)}
                controls autoPlay
                className="max-w-full max-h-[85vh] rounded-xl shadow-2xl"
              />
            ) : (
              <img
                src={resolveMediaUrl(currentItem.url)}
                alt={`Gallery ${lightboxIdx + 1}`}
                className="max-w-full max-h-[85vh] rounded-xl object-contain shadow-2xl"
              />
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <ChevronRight size={28} className="text-white" />
          </button>
        </div>
      )}
    </div>
  );
};

export default GallerySettings;
